import { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import {
    init,
    registerStyles,
    dispose,
    Chart,
    DeepPartial,
    Styles,
    Nullable,
    LineType,
    TooltipShowType,
    TooltipShowRule,
    PolygonType,
    KLineData,
    registerOverlay,
    registerFigure,
    OverlayCreate,
    TooltipLegend,
    registerLocale,
    Locales,
} from "klinecharts";
import { BTN_OBJ2, BTN_OBJ } from "./constants";
import { cloneDeep } from "lodash-es";
import { useCommonState } from "~/store";
import { initData, themeParams, useSignal } from "@telegram-apps/sdk-react";
import { fetchTokenCandles } from "~/api/endpoints";
import useSWR from "swr";
import { useSnackbar } from "~/Provider/Snackbar";
import { TokenCandle } from "~/types";
import { formatNumber } from "~/utils/number-format";
import { ORDER_DIRECTION } from "../constants";
import BigNumber from "bignumber.js";

const red = "#FE3B30";
const green = "#31D158";
const alphaRed = "rgba(254, 59, 48, .7)";
const alphaGreen = "rgba(49, 209, 88, .7)";
const lightBorder1 = "#efeff4";
const darkBorder1 = "#2f2f2f";

const SIZE = 200;

export default function Kline({
    activeCode,
    chain_id,
    contract,
    precision,
    initialData,
    visible,
    isLargeMode,
    setIsLargeMode,
    orderList,
}: any) {
    const { isIphone } = useCommonState();
    const isDark = useSignal(themeParams.isDark);
    const [currLBtn, setCurrLBtn] = useState("");
    const [currRBtn, setCurrRBtn] = useState("");
    const refChart = useRef<Chart | null>(null);
    const refSubPane = useRef<Nullable<string>>();
    const firstRef = useRef(true);
    const initializedRef = useRef(false);
    const hasMoreKData = useRef<boolean>(false);
    const snapbar = useSnackbar();
    const initDataState = useSignal(initData.state);
    const userLanguageCode = initDataState?.user?.languageCode;
    const isCN =
        userLanguageCode && ["zh-hans", "zh-hant"].includes(userLanguageCode);

    const optionLight = {
        grid: {
            show: true,
            horizontal: {
                show: true,
                size: 0.5,
                color: lightBorder1,
                style: LineType.Solid,
            },
            vertical: {
                show: true,
                size: 0.5,
                color: lightBorder1,
                style: LineType.Solid,
            },
        },
        candle: {
            bar: {
                upColor: green,
                upWickColor: green,
                upBorderColor: green,
                downColor: red,
                downWickColor: red,
                downBorderColor: red,
                noChangeColor: "#888888",
                noChangeBorderColor: "#888888",
                noChangeWickColor: "#888888",
            },
            priceMark: {
                show: true,
                last: {
                    upColor: green,
                    downColor: red,
                    noChangeColor: "#888888",
                    text: {
                        paddingTop: isIphone ? 1 : 3,
                        paddingBottom: 4,
                        size: 12,
                    },
                },
            },
            tooltip: {
                showType: TooltipShowType.Rect,
                showRule: TooltipShowRule.FollowCross,
                custom: ({ current }: { current: KLineData }) => {
                    const tips: TooltipLegend[] = [
                        { title: "time", value: dayjs(current.timestamp).format("MM-DD HH:mm") },
                        { title: "open", value: "{open}" },
                        { title: "high", value: "{high}" },
                        { title: "low", value: "{low}" },
                        { title: "close", value: "{close}" },
                        { title: "volume", value: "{volume}" },
                    ];
                    if (current.avgBuyPrice) {
                        tips.push({
                            title: {
                                text: "avgBuyPrice",
                                color: green,
                            },
                            value: `${formatNumber(current.avgBuyPrice)}`,
                        });
                    }
                    if (current.avgSellPrice) {
                        tips.push({
                            title: {
                                text: "avgSellPrice",
                                color: red,
                            },
                            value: `${formatNumber(current.avgSellPrice)}`,
                        });
                    }
                    return tips;
                },
            },
        },
        crosshair: {
            horizontal: {
                text: {
                    paddingTop: isIphone ? 1 : 3,
                    paddingBottom: 4,
                    size: 12,
                },
            },
            vertical: {
                text: {
                    paddingTop: isIphone ? 1 : 3,
                    paddingBottom: 4,
                    size: 12,
                },
            },
        },
        indicator: {
            ohlc: {
                upColor: alphaGreen,
                downColor: alphaRed,
                noChangeColor: "#888888",
            },
            bars: [
                {
                    style: PolygonType.Fill,
                    borderStyle: LineType.Solid,
                    borderSize: 1,
                    borderDashedValue: [2, 2],
                    upColor: alphaGreen,
                    downColor: alphaRed,
                    noChangeColor: "#888888",
                },
            ],
            circles: [
                {
                    style: PolygonType.Fill,
                    borderStyle: LineType.Solid,
                    borderSize: 1,
                    borderDashedValue: [2, 2],
                    upColor: alphaGreen,
                    downColor: alphaRed,
                    noChangeColor: "#888888",
                },
            ],
            tooltip: {
                text: {
                    size: 10,
                },
            },
        },
        xAxis: {
            show: true,
            axisLine: {
                color: lightBorder1,
            },
            tickLine: {
                color: lightBorder1,
            },
        },
        yAxis: {
            show: true,
            axisLine: {
                color: lightBorder1,
            },
            tickLine: {
                color: lightBorder1,
            },
            inside: true,
            tickText: {
                show: true,
            },
        },
        // 图表之间的分割线
        separator: {
            size: 1,
            color: lightBorder1,
            fill: true,
            activeBackgroundColor: "rgba(230, 230, 230, .15)",
        },
    };
    const optionDark = cloneDeep(optionLight);
    optionDark.grid.horizontal.color = darkBorder1;
    optionDark.grid.vertical.color = darkBorder1;
    optionDark.xAxis.axisLine.color = darkBorder1;
    optionDark.xAxis.tickLine.color = darkBorder1;
    optionDark.yAxis.axisLine.color = darkBorder1;
    optionDark.yAxis.tickLine.color = darkBorder1;
    optionDark.separator.color = darkBorder1;

    const switchThemeKline = (isDark: boolean) => {
        if (refChart.current) {
            refChart.current.setStyles(
                isDark
                    ? "green_rise_red_fall_dark"
                    : "green_rise_red_fall_light"
            );
        }
    };

    useEffect(() => {
        if (firstRef.current) {
            firstRef.current = false;
            return;
        }
        switchThemeKline(isDark);
    }, [isDark]);

    const queryCandles = (args: any) => {
        const [, chain_id, contract, bar_type, ts, size] = args;
        return fetchTokenCandles(chain_id, contract, bar_type, ts, size).then(
            (res) => {
                const { result = [], err_msg } = res?.data || {};
                if (err_msg && err_msg.length > 0) {
                    snapbar.error(err_msg);
                }
                return result;
            }
        );
    };

    const { data: lastOneData } = useSWR(
        [
            "k-detail:api-kline-chart-last-one",
            chain_id,
            contract,
            activeCode,
            0,
            1,
        ],
        queryCandles,
        {
            fallbackData: [],
            refreshInterval: 8000,
        }
    );

    useEffect(() => {
        if (!lastOneData || lastOneData.length === 0) return;
        const chartData = lastOneData.map((item) => ({
            open: item.open_usd,
            close: item.close_usd,
            low: item.low_usd,
            high: item.high_usd,
            volume: item.volume_usd,
            timestamp: item.ts * 1000,
        }));
        refChart.current?.updateData(chartData[0]);
        hasMoreKData.current = lastOneData.length === SIZE;
    }, [lastOneData]);

    const fetchMoreData = (ts: number) => {
        if (!visible) return;
        console.log("fetch more", ts);
        return queryCandles([
            "k-detail:api-kline-chart-last-one",
            chain_id,
            contract,
            activeCode,
            ts,
            200,
        ]).then((moreData) => {
            console.log(" more data", moreData);
            if (!moreData || moreData.length === 0) return;
            const chartData = moreData.map((item) => ({
                open: item.open_usd,
                close: item.close_usd,
                low: item.low_usd,
                high: item.high_usd,
                volume: item.volume_usd,
                timestamp: item.ts * 1000,
            }));
            refChart.current?.applyMoreData(chartData, true);
        });
    };

    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        registerStyles(
            "green_rise_red_fall_light",
            optionLight as DeepPartial<Styles>
        );
        registerStyles(
            "green_rise_red_fall_dark",
            optionDark as DeepPartial<Styles>
        );

        function drawRoundedRect(
            ctx: CanvasRenderingContext2D,
            x: number,
            y: number,
            width: number,
            height: number,
            radius: number
        ) {
            ctx.beginPath();
            // 左上角
            ctx.moveTo(x + radius, y);
            // 右上角
            ctx.lineTo(x + width - radius, y);
            ctx.arcTo(x + width, y, x + width, y + radius, radius);
            // 右下角
            ctx.lineTo(x + width, y + height - radius);
            ctx.arcTo(
                x + width,
                y + height,
                x + width - radius,
                y + height,
                radius
            );
            // 左下角
            ctx.lineTo(x + radius, y + height);
            ctx.arcTo(x, y + height, x, y + height - radius, radius);
            // 左上角
            ctx.lineTo(x, y + radius);
            ctx.arcTo(x, y, x + radius, y, radius);
            ctx.closePath();
        }

        registerFigure({
            name: "position",
            draw: (ctx, attrs, styles) => {
                const { x, y, text, verticalAlign } = attrs as any;
                const width = 12,
                    height = 12;
                const halfWidth = width / 2;
                const { color } = styles as any;
                if (verticalAlign === "top") {
                    // 设置矩形样式
                    drawRoundedRect(
                        ctx,
                        x - halfWidth,
                        y + 4,
                        width,
                        height,
                        2
                    );
                    ctx.fillStyle = color;
                    ctx.fill();

                    ctx.fillStyle = "#ffffff";
                    ctx.font = `bold 10px Arial`;
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    const centerX = x;
                    const centerY = y + 4 + height / 2;
                    ctx.fillText(text, centerX, centerY);
                } else {
                    drawRoundedRect(
                        ctx,
                        x - halfWidth,
                        y - height - 4,
                        width,
                        height,
                        2
                    );
                    ctx.fillStyle = color;
                    ctx.fill();

                    ctx.fillStyle = "#ffffff";
                    ctx.font = `bold 10px Arial`;
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    const centerX = x;
                    const centerY = y - 4 - height / 2;
                    ctx.fillText(text, centerX, centerY);
                }
            },
            checkEventOn: (coordinate, attrs) => {
                const { x, y } = coordinate;
                const {
                    width,
                    height,
                    x: posX,
                    y: posY,
                    verticalAlign,
                } = attrs as any;
                if (verticalAlign === "top") {
                    return (
                        x >= posX - width / 2 &&
                        x <= posX + width / 2 &&
                        y >= posY &&
                        y <= posY + height
                    );
                }
                return (
                    x >= posX - width / 2 &&
                    x <= posX + width / 2 &&
                    y >= posY - height &&
                    y <= posY
                );
            },
        });
        registerOverlay({
            name: "buyPosition",
            lock: true,
            createPointFigures: ({ coordinates, barSpace }) => {
                return {
                    type: "position",
                    attrs: {
                        x: coordinates[0].x,
                        y: coordinates[0].y,
                        width: barSpace.bar,
                        height: barSpace.bar,
                        text: "B",
                        verticalAlign: "bottom",
                    },
                    styles: { color: green },
                };
            },
        });
        registerOverlay({
            name: "sellPosition",
            lock: true,
            createPointFigures: ({ coordinates, barSpace }) => {
                return {
                    type: "position",
                    attrs: {
                        x: coordinates[0].x,
                        y: coordinates[0].y,
                        width: barSpace.bar,
                        height: barSpace.bar,
                        text: "S",
                        verticalAlign: "top",
                    },
                    styles: { color: red },
                };
            },
        });

        refChart.current = init("k-line-chart");
        refChart.current?.setLocale(isCN ? "zh-CN" : "en-US");
        refChart.current?.setPriceVolumePrecision(precision, 0);
        // refChart.current?.setDecimalFold({
        //     format(value) {
        //         if (/^\d*\.?\d*$/.test(String(value))) {
        //             return formatNumber(value, { bigNumThreshold: 1e9 });
        //         }

        //         return String(value);
        //     },
        // });
        registerLocale("zh-CN", {
            avgSellPrice: "卖出均价：",
            avgBuyPrice: "买入均价：",
        } as unknown as Locales);
        registerLocale("en-US", {
            avgSellPrice: "Average Selling Price:",
            avgBuyPrice: "Average Purchase Price:",
        } as unknown as Locales);

        const chartData = initialData.map(
            (item: TokenCandle) =>
                ({
                    timestamp: item.ts * 1000,
                    open: item.open_usd,
                    close: item.close_usd,
                    low: item.low_usd,
                    high: item.high_usd,
                    volume: item.volume_usd,
                }) as KLineData
        );

        refChart.current?.loadMore(() => {
            const dataList = refChart.current?.getDataList() ?? [];
            const ts = dataList[0].timestamp;
            if (ts && hasMoreKData.current) {
                fetchMoreData(ts / 1000);
            }
        });

        refChart.current?.applyNewData(chartData);
        hasMoreKData.current = initialData.length === SIZE;
        switchThemeKline(isDark);

        refChart.current?.loadMore(() => {
            const dataList = refChart.current?.getDataList() ?? [];
            const ts = dataList[0].timestamp;
            console.log("loadMore ts", ts);
            if (ts && hasMoreKData.current) {
                fetchMoreData(ts / 1000);
            }
        });

        const resizeChart = () => refChart.current?.resize();
        const visibilitychange = () => {
            if (document.visibilityState === "visible") {
                resizeChart();
            }
        };

        document.addEventListener("visibilitychange", visibilitychange);
        window.addEventListener("resize", resizeChart);
        return () => {
            dispose("chart");
            document.removeEventListener("visibilitychange", visibilitychange);
            window.removeEventListener("resize", resizeChart);
        };
    }, []);

    const refFirst = useRef(true);
    const prevActiveCode = useRef<string>(activeCode);
    useEffect(() => {
        if (refFirst.current) {
            refFirst.current = false;
            return;
        }

        if (prevActiveCode !== activeCode) {
            const chartData = initialData.map((item: TokenCandle) => ({
                open: item.open_usd,
                close: item.close_usd,
                low: item.low_usd,
                high: item.high_usd,
                volume: item.volume_usd,
                timestamp: item.ts * 1000,
            }));

            refChart.current?.applyNewData(chartData);
            hasMoreKData.current = initialData.length === SIZE;
            prevActiveCode.current = activeCode;
        }
    }, [activeCode, initialData]);

    // 画买入卖出点
    useEffect(() => {
        refChart.current?.removeOverlay({
            name: "buyPosition",
        });
        refChart.current?.removeOverlay({
            name: "sellPosition",
        });
        if (orderList?.length > 0 && initialData?.length > 0) {
            const startTime = initialData[0].ts;

            const positions: {
                [key: string]: { buy: any[]; sell: any[]; collumn: any };
            } = {};
            orderList.forEach((order: any) => {
                const createTime = +order.create_time;
                if (createTime >= startTime) {
                    const p = initialData.find((item: any, index: number) => {
                        return (
                            index === initialData.length - 1 ||
                            (createTime >= item.ts &&
                                createTime < initialData[index + 1]?.ts)
                        );
                    });
                    if (p) {
                        if (!positions[p.ts]) {
                            positions[p.ts] = {
                                collumn: p,
                                buy: [],
                                sell: [],
                            };
                        }
                        positions[p.ts][
                            order.direction === ORDER_DIRECTION.buy
                                ? "buy"
                                : "sell"
                        ].push(order);
                    }
                }
            });
            const list: OverlayCreate[] = [];
            Object.keys(positions).forEach((ts: string) => {
                const buyOrders = positions[ts].buy;
                const sellOrders = positions[ts].sell;
                if (buyOrders.length > 0) {
                    list.push({
                        name: "buyPosition",
                        points: [
                            {
                                timestamp: +ts * 1000,
                                value: positions[ts].collumn.high_usd,
                            },
                        ],
                        extendData: buyOrders,
                    });
                }
                if (sellOrders.length > 0) {
                    list.push({
                        name: "sellPosition",
                        points: [
                            {
                                timestamp: +ts * 1000,
                                value: positions[ts].collumn.low_usd,
                            },
                        ],
                        extendData: sellOrders,
                    });
                }
            });
            if (list.length > 0) {
                refChart.current?.createOverlay(list);
                const chartData = initialData.map((item: TokenCandle) => {
                    let avgBuyPrice = "";
                    let avgSellPrice = "";
                    const buyOrders = positions[item.ts]?.buy.length || 0;
                    const sellOrders = positions[item.ts]?.sell.length || 0;
                    if (buyOrders > 0) {
                        let totalAmount = new BigNumber(0);
                        avgBuyPrice = positions[item.ts]?.buy
                            .reduce((pre: BigNumber, cur: any) => {
                                const v = new BigNumber(
                                    cur.uint_price
                                ).multipliedBy(cur.to_token.amount);
                                totalAmount = totalAmount.plus(
                                    cur.to_token.amount
                                );
                                return pre.plus(v);
                            }, new BigNumber(0))
                            .div(totalAmount)
                            .toFixed();
                    }
                    if (sellOrders > 0) {
                        let totalAmount = new BigNumber(0);
                        avgSellPrice = positions[item.ts]?.sell
                            .reduce((pre: BigNumber, cur: any) => {
                                const v = new BigNumber(
                                    cur.uint_price
                                ).multipliedBy(cur.from_token.amount);
                                totalAmount = totalAmount.plus(
                                    cur.from_token.amount
                                );
                                return pre.plus(v);
                            }, new BigNumber(0))
                            .div(totalAmount)
                            .toFixed();
                    }
                    return {
                        open: item.open_usd,
                        close: item.close_usd,
                        low: item.low_usd,
                        high: item.high_usd,
                        volume: item.volume_usd,
                        timestamp: item.ts * 1000,
                        avgBuyPrice,
                        avgSellPrice,
                        buyOrders,
                        sellOrders,
                    };
                });
                refChart.current?.applyNewData(chartData);
            }
        }
    }, [orderList, initialData]);

    // 切换到 MA
    function switchToMA() {
        refChart.current?.removeIndicator("candle_pane", "BOLL");
        refChart.current?.createIndicator({ name: "MA", precision }, false, {
            id: "candle_pane",
        });
    }
    // 切换到 BOLL
    function switchToBOLL() {
        refChart.current?.removeIndicator("candle_pane", "MA");
        refChart.current?.createIndicator({ name: "BOLL", precision }, false, {
            id: "candle_pane",
        });
    }

    const lBtnEvent = (key: string) => {
        if (key === currLBtn) {
            refChart.current?.removeIndicator("candle_pane", key);
            setCurrLBtn("");
        } else {
            if (key === BTN_OBJ2.MA) {
                switchToMA();
            } else if (key === BTN_OBJ2.BOLL) {
                switchToBOLL();
            }
            setCurrLBtn(key);
        }
    };

    function switchIndicator(key: string) {
        Object.keys(BTN_OBJ).forEach((item) => {
            if (refSubPane.current) {
                refChart.current?.removeIndicator(refSubPane.current, item);
            }
        });
        refSubPane.current = refChart.current?.createIndicator({
            name: key,
            precision: precision,
        });
    }
    const rBtnEvent = (key: string) => {
        if (key === currRBtn) {
            if (refSubPane.current) {
                refChart.current?.removeIndicator(refSubPane.current, key);
            }
            setCurrRBtn("");
            setIsLargeMode(false);
        } else {
            setIsLargeMode(true);
            switchIndicator(key);
            refChart.current?.resize();
            setCurrRBtn(key);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            refChart.current?.resize();
        });
    }, [isLargeMode]);

    return (
        <div className="w-full">
            <div
                id="k-line-chart"
                className={clsx(isLargeMode ? "h-[350px]" : "h-[250px]")}
            />

            <div className="flex items-center justify-between h-[34px] border-y-[0.5px] border-border_1">
                <div className="flex">
                    {Object.keys(BTN_OBJ2).map((item) => (
                        <div
                            key={item}
                            className={clsx(
                                "text-direction2 font-Regular cursor-pointer px-[16px]",
                                item === currLBtn
                                    ? "text-text_1"
                                    : "text-text_3"
                            )}
                            onClick={() => lBtnEvent(item)}
                        >
                            {item}
                        </div>
                    ))}
                </div>
                <div className="flex">
                    {Object.keys(BTN_OBJ).map((item) => (
                        <div
                            key={item}
                            className={clsx(
                                "text-direction2 font-Regular cursor-pointer px-[16px]",
                                item === currRBtn
                                    ? "text-text_1"
                                    : "text-text_3"
                            )}
                            onClick={() => rBtnEvent(item)}
                        >
                            {item}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
